import { arrayResultMemoizeOptions } from 'checkout/lib/utils/misc/arrayResultMemoizeOptions'
import { excludeNullOrUndefined } from 'checkout/utils'
import { isCarHireItem } from 'lib/checkout'
import createSelector from 'lib/createSelector'
import { sum } from 'lib/arrayUtils'
import { getCarHireItemView } from 'checkout/lib/utils/carHire/view'
import { DMY_CASUAL_FORMAT, SHORT_TIME_FORMAT_AM_PM } from 'constants/dateFormats'
import { pluralizeToString } from 'lib/pluralize'
import { makeDateTime } from 'tripPlanner/utils'
import { isCarHireInsuranceAvailable } from 'selectors/carHireSelectors'

function getCarHireInsuranceItem(carHireInsurance: App.CarHireInsurance): App.Checkout.ItemBreakdownView {
  return {
    title: 'Insurance',
    itemType: 'car_hire',
    additionalInfoText: [],
    additionalElements: [{
      id: carHireInsurance.id,
      left: {
        type: 'text',
        value: 'Enhanced protection',
      },
      right: {
        type: 'price',
        value: carHireInsurance.total || 0,
      },
    }],
  }
}

function getCarHireAddonsItems(selectedAddons: Record<string, App.CartCarHireAddon>, carView: App.Checkout.CarHireItemView): App.Checkout.ItemBreakdownView {
  return {
    title: 'Add-ons',
    itemType: 'car_hire',
    additionalInfoText: [],
    taxesAndFees: carView.taxesAndFees,
    additionalElements: Object.values(selectedAddons).map(addon => {
      return {
        id: addon.description || '',
        left: {
          type: 'text',
          value: addon.description || '',
          quantity: addon.quantity || 1,
        },
        right: {
          type: 'price',
          value: addon.total * (addon.quantity || 1),
        },
      }
    }),
  }
}

export const getCarHireItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.CarHireItem> => items.filter(isCarHireItem),
  arrayResultMemoizeOptions,
)

export const getCarHireItemsView = createSelector(
  (state: App.State) => getCarHireItems(state),
  (
    carHireItems,
  ): App.WithDataStatus<Array<App.Checkout.CarHireItemView>> => {
    const itemViews = carHireItems.map(item => {
      return getCarHireItemView(item)
    })

    return {
      hasRequiredData: itemViews.every(Boolean),
      data: itemViews.filter(excludeNullOrUndefined),
    }
  },
)

export const getSelectedCarHireInsurance = createSelector(
  (state: App.State) => state.checkout.cart.carHireInsurance,
  (carHireInsurance) => carHireInsurance,
)

export const getSelectedAddons = createSelector(
  (state: App.State) => state.checkout.cart.carHireAddons,
  (addons) => addons,
)

export const getCarHireBreakdownView = createSelector(
  getCarHireItemsView,
  getSelectedAddons,
  getSelectedCarHireInsurance,
  isCarHireInsuranceAvailable,
  (viewWithStatus, selectedAddons, selectedCarHireInsurance, isCarHireInsuranceAvailable)
  : App.WithDataStatus<Array<App.Checkout.PriceBreakdownView>> => {
    if (viewWithStatus.data.length === 0) {
      return {
        hasRequiredData: viewWithStatus.hasRequiredData,
        data: [],
      }
    }

    const hasSelectedAddons = Object.keys(selectedAddons).length > 0
    const carViews = viewWithStatus.data.filter(excludeNullOrUndefined)
    const carView = carViews[0]
    const { offer } = carView.item
    const isSameLocation = offer.pickUp.name === offer.dropOff.name

    const carHireBreakdownView: App.Checkout.PriceBreakdownView = {
      title: carView.model,
      price: sum(viewWithStatus.data.map(i => i?.price ?? 0)),
      description: isSameLocation ? `Pick up and drop-off at ${offer.pickUp.name}` : `Pick up at ${offer.pickUp.name} and drop-off at ${offer.dropOff.name}`,
      additionalInfoText: [
        `${makeDateTime(carView.item.pickUpDate, carView.item.pickUpTime).format(`${DMY_CASUAL_FORMAT}, ${SHORT_TIME_FORMAT_AM_PM}`)} → ${makeDateTime(carView.item.dropOffDate, carView.item.dropOffTime).format(`${DMY_CASUAL_FORMAT}, ${SHORT_TIME_FORMAT_AM_PM}`)}`,
        `${pluralizeToString('day', offer.duration)}`,
      ],
      items: [
        ...isCarHireInsuranceAvailable && selectedCarHireInsurance && selectedCarHireInsurance.type !== 'limited' ?
            [getCarHireInsuranceItem(selectedCarHireInsurance)] : [],
        ...hasSelectedAddons ? [getCarHireAddonsItems(selectedAddons, carView)] : [],
      ],
      itemsAlwaysVisible: true,
    }

    return {
      hasRequiredData: true,
      data: [carHireBreakdownView],
    }
  },
)
